import { useCallback, useMemo } from 'react'

import { type UseQueryResult, useQueries } from '@tanstack/react-query'
import { identity } from 'es-toolkit'

import { combineArrayQueriesWithPending } from '@/helpers/api'
import { getKnowledgeBaseLabels } from '@/helpers/knowledge-base'

import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { fetchOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'
import { pursuitFilesQueryKeys } from '@/hooks/pursuits/types'
import { fetchPursuitFiles } from '@/hooks/pursuits/usePursuitFiles'
import { useTags } from '@/hooks/tags/useTags'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { PotentiallyUnhydratedFile, UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { Tag } from '@/types/Tag'

interface Result<T> {
  data: T
  isLoading: boolean[]
  anyLoading: boolean
  isPending: boolean[]
  anyPending: boolean
  isFetching: boolean[]
  anyFetching: boolean
  isFetched: boolean[]
  allFetched: boolean
}

interface UseKnowledgeBaseFilesArgs<T> {
  orgId: UUID | ''
  pursuitId?: UUID | ''
  select?: (data: PotentiallyUnhydratedFile[]) => PotentiallyUnhydratedFile[]
  combine?: (
    result: UseQueryResult<PotentiallyUnhydratedFile[], Error>[],
  ) => Result<T>
}

type UseKnowledgeBaseFileLabelsArgs = UseKnowledgeBaseFilesArgs<string[]>

const combineAndSort = (
  result: UseQueryResult<PotentiallyUnhydratedFile[], Error>[],
) => {
  const combined = combineArrayQueriesWithPending(result)
  return {
    ...combined,
    data: combined.data.sort((a, b) => a.name.localeCompare(b.name)),
  }
}

export const useKnowledgeBaseFiles = <T = PotentiallyUnhydratedFile[]>({
  orgId,
  pursuitId,
  select = identity,
  combine = combineAndSort as unknown as (
    result: UseQueryResult<PotentiallyUnhydratedFile[], Error>[],
  ) => Result<T>,
}: UseKnowledgeBaseFilesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selector = useCallback(
    (data: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      return select(data.data)
    },
    [select],
  )

  const query = useQueries({
    queries: [
      {
        queryKey: pursuitFilesQueryKeys.pursuit({ pursuitId: pursuitId ?? '' }),
        queryFn: async () => {
          const response = await fetchPursuitFiles(
            pursuitId ?? '',
            accessToken,
            { limit: 10000 },
          )
          return response
        },
        enabled: !!pursuitId,
        staleTime: Infinity,
        select: selector,
      },
      {
        queryKey: organizationFilesQueryKeys.organization({ orgId }),
        queryFn: () =>
          fetchOrganizationFiles(orgId, accessToken, { limit: 10000 }),
        enabled: !!orgId,
        staleTime: Infinity,
        select: selector,
      },
    ],
    combine,
  })

  return query
}

export const combineFilesToLabels =
  (orgTags: Tag[]) =>
  (results: UseQueryResult<PotentiallyUnhydratedFile[], Error>[]) => {
    const data = results.reduce(
      (acc: Array<PotentiallyUnhydratedFile>, result) => {
        return [...acc, ...(result.data ?? [])]
      },
      [],
    )

    const labels = getKnowledgeBaseLabels(data, orgTags)

    const isLoading = results.map((result) => result.isLoading)
    const anyLoading = isLoading.some((loading) => loading)

    const isPending = results.map((result) => result.isPending)
    const anyPending = isPending.some((pending) => pending)

    const isFetching = results.map((result) => result.isFetching)
    const anyFetching = isFetching.some((fetching) => fetching)

    const isFetched = results.map((result) => result.isFetched)
    const allFetched = isFetched.every((fetched) => fetched)

    return {
      data: labels,
      isLoading,
      anyLoading,
      isPending,
      anyPending,
      isFetching,
      anyFetching,
      isFetched,
      allFetched,
    }
  }

export const useKnowledgeBaseFileLabels = (
  args: UseKnowledgeBaseFileLabelsArgs,
) => {
  const { data: orgTags } = useTags({ orgId: args.orgId })
  return useKnowledgeBaseFiles({
    ...args,
    combine: args.combine ?? combineFilesToLabels(orgTags ?? []),
  })
}
